import React, { useState } from 'react';
import { Calendar, Clock, MapPin, Users, X, Trash2, UserMinus, Mail } from 'lucide-react';
import { GiTennisBall } from 'react-icons/gi';
import { FaWhatsapp } from 'react-icons/fa';
import { GameProposal, Player, Location } from '../types'; // Add Location import
import { formatGameDate } from '../utils/dateUtils';
import { getGameGenderLabel } from '../utils/formatters';
import Modal from './modals/Modal';
import { formatDisplayName } from '../utils/nameUtils';

interface GameDetailsProps {
  game: GameProposal;
  onClose: () => void;
  currentUser: Player | null;
  onEdit?: () => void;
  onDelete?: (gameId: string) => void;
  onRemovePlayer?: (gameId: string, playerId: string) => void;
  locations: Location[];
}

export default function GameDetails({ 
  game, 
  onClose, 
  currentUser, 
  onEdit, 
  onDelete,
  onRemovePlayer,
  locations
}: GameDetailsProps) {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  
  // Handle createdBy being either string or object
  const createdById = typeof game.createdBy === 'string' ? game.createdBy : game.createdBy?.id;
  const isCreator = currentUser?.id === createdById;

  const handleRemovePlayer = (playerId: string) => {
    onRemovePlayer?.(game.id, playerId);
    onClose();
  };

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = () => {
    onDelete?.(game.id);
    setShowDeleteConfirmation(false);
    onClose();
  };

  const getGenderLabel = (gender: string | undefined) => {
    if (!gender) return null;
    return gender === 'male' ? 'Masculino' : 'Feminino';
  };

  // Handler de clique do WhatsApp
  const handleWhatsAppClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!currentUser) {
      e.preventDefault();
      alert('Você precisa estar logado para conversar no WhatsApp.');
    }
  };

  // In the getPlayerCategories function - Update to handle both permanent and temporary players
  const getPlayerCategories = (player: Player) => {
    const categories = [];
    const playingSide = getPlayingSideLabel(player.playing_side);
  
    // Ensure category is shown for the current game's sport
    if (game.sport === 'tennis' && player.tennis_category) {
      categories.push(`Tênis: ${player.tennis_category}${playingSide ? ` (${playingSide})` : ''}`);
    }
    else if (game.sport === 'beach-tennis' && player.beach_tennis_category) {
      categories.push(`Beach Tennis: ${player.beach_tennis_category}${playingSide ? ` (${playingSide})` : ''}`);
    }
    else if (game.sport === 'padel' && player.padel_category) {
      categories.push(`Padel: ${player.padel_category}${playingSide ? ` (${playingSide})` : ''}`);
    }
  
    // Fallback for incomplete player data
    if (!categories.length && player.category) {
      const sport = game.sport === 'padel' ? 'Padel' : 
                   game.sport === 'beach-tennis' ? 'Beach Tennis' : 
                   'Tênis';
      categories.push(`${sport}: ${player.category}${playingSide ? ` (${playingSide})` : ''}`);
    }
  
    return categories;
  };

  // Update getPlayingSideLabel to handle database values
  const getPlayingSideLabel = (side: string | undefined) => {
    if (!side) return null;
    return side === 'both' ? 'Ambos os lados' : 
           side === 'left' ? 'Lado Esquerdo' : 
           side === 'right' ? 'Lado Direito' : null;
  };

  // Add this function
  const getLocationNames = () => {
    if (!game.locations || !locations) return 'Local não encontrado';
    
    return game.locations.map(locationId => {
      const location = locations.find(loc => loc.id === locationId);
      return location ? location.name : 'Local não encontrado';
    }).join(' / ');
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-40">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Detalhes do Jogo</h2>
              <div className="flex items-center gap-2">
                {isCreator && (
                  <>
                    <button
                      onClick={onEdit}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      Editar
                    </button>
                    <button
                      onClick={handleDelete}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-xl font-semibold">
                      {game.sport === 'padel' ? 'Padel' : 
                       game.sport === 'beach-tennis' ? 'Beach Tennis' : 
                       'Tênis'}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                      game.status === 'open'
                        ? 'bg-green-100 text-green-800'
                        : game.status === 'full'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {game.status === 'open' ? 'Aberto' : game.status === 'full' ? 'Completo' : 'Cancelado'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {getGameGenderLabel(game.gender)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Categorias: {game.requiredCategories.join(', ')}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-5 h-5 mr-2" />
                  <span>{formatGameDate(game.date)}</span>
                </div>

                <div className="flex items-center text-gray-600">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>
                    {(game.startTime || game.start_time || game.time)?.substring(0, 5)} - {(game.endTime || game.end_time)?.substring(0, 5)}
                  </span>
                </div>

                <div className="flex items-center text-gray-600">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>{getLocationNames()}</span>
                </div>

                <div className="flex items-center text-gray-600">
                  <Users className="w-5 h-5 mr-2" />
                  <span>
                    {game.is_organizer_only ? game.players.length - 1 : game.players.length} / {game.maxPlayers} jogadores
                  </span>
                </div>

                {/* Add visibility information */}
                <div className="flex items-center text-gray-600">
                  <span className="w-5 h-5 mr-2 flex items-center justify-center">
                    {game.is_public ? '🌐' : '👥'}
                  </span>
                  <span>
                    {game.is_public ? 'Público' : 'Privado'}
                    {!game.is_public && game.groups && game.groups.length > 0 && (
                      <span className="ml-1">
                        ({game.groups.map(g => g.name).join(', ')})
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {game.description && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Informações Adicionais</h4>
                  <p className="text-gray-600 whitespace-pre-wrap">{game.description}</p>
                </div>
              )}

              {game.extra_cost && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Custos Adicionais</h4>
                  <p className="text-gray-600 whitespace-pre-wrap">{game.extra_cost}</p>
                </div>
              )}

              <div className="mt-6">
                <h4 className="font-medium text-gray-900 mb-4">Participantes</h4>
                <div className="space-y-3">
                  {game.players
                    .filter(player => !(game.is_organizer_only && player.id === createdById))
                    .map((player) => (
                    <div
                      key={player.id}
                      className="flex flex-col bg-gray-50 p-3 rounded-lg"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          {player.avatar ? (
                            <div className="w-10 h-10 rounded-full overflow-hidden">
                              <img
                                src={player.avatar}
                                alt={`Avatar de ${player.name}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">
                                {player.category?.split(' ')[1]}  {/* Show category number */}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{formatDisplayName(player.name)}</p>
                            <div className="text-sm text-gray-500 space-y-1">
                              {getPlayerCategories(player).map((category, index) => (
                                <span key={index} className="block">{category}</span>
                              ))}
                              {player.gender && (
                                <span className="block">{getGenderLabel(player.gender)}</span>
                              )}
                              
                              {/* Remove this block: */}
                              {/* {(player.playing_side || player.playingSide) && (
                                <span className="block">{getPlayingSideLabel(player.playing_side || player.playingSide)}</span>
                              )} */}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-4">
                          <div className="text-right">
                            {player.phone && player.phone.trim() !== '' && (
                              <a 
                                href={currentUser ? `https://wa.me/55${player.phone.replace(/\D/g, '')}` : '#'} 
                                onClick={handleWhatsAppClick}
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className={`inline-flex items-center ${
                                  currentUser 
                                    ? 'text-green-600 hover:text-green-700' 
                                    : 'text-gray-400 cursor-not-allowed'
                                } transition-colors`}
                                title={currentUser ? `Conversar com ${player.name} no WhatsApp` : 'Faça login para conversar no WhatsApp'}
                              >
                                <FaWhatsapp className="w-5 h-5" />
                              </a>
                            )}
                            {player.email && player.email.trim() !== '' && (
                              <a
                                href={`mailto:${player.email}`}
                                className="inline-block text-gray-500 hover:text-gray-700 transition-colors ml-2"
                                title={`Enviar email para ${player.name}`}
                              >
                                <Mail className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                          {(isCreator || (currentUser?.id === player.id)) && player.id !== createdById && (
                            <button
                              onClick={() => handleRemovePlayer(player.id)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                              title={currentUser?.id === player.id ? "Sair do jogo" : "Remover jogador"}
                            >
                              <UserMinus className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                      {player.joinMessage && (
                        <div className="mt-2 text-sm text-gray-600 bg-white p-2 rounded">
                          "{player.joinMessage}"
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDeleteConfirmation && (
        <Modal
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          title="Excluir Jogo"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Tem certeza que deseja excluir este jogo?
            </p>
            <p className="text-sm text-blue-600">
              Dica: Você poderá republicar este jogo mais tarde acessando seu histórico no perfil.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}